import React, { useState } from 'react';
import { createSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ToolBar } from '@1c/ui';
import { useNavigate } from 'src/utils/useNavigate';
import { FinalField, FinalForm } from 'src/components/FinalForm';
import PhoneInput from 'src/components/PhoneInput';
import Button from 'src/components/Button';
import { postAuthorizationStartAPI } from 'src/serverApi/authorization';
import { getAuthorizationInfoAPI } from 'src/serverApi/authorization';
import { postAcceptInvitationToOrganizationAPI } from 'src/serverApi/organizationUsers';
import routes from 'src/constants/routes';
import styles from './styles.module.scss';

type Props = {
  inviteOrganizationUeid?: string;
  inviteConfirmationId?: string | null | undefined;
  onSubmit: (authorizationInfo: any) => void;
};

const LoginForm: React.FC<Props> = ({ inviteConfirmationId, inviteOrganizationUeid, onSubmit }) => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isEmptyPhone, setIsEmptyPhone] = useState(true);
  const [validationError, setValidationError] = useState('');

  const startRegistration = async (data: any) => {
    try {
      setValidationError('');
      setIsLoading(true);

      const sessionState = await postAuthorizationStartAPI(data);
      const authorizationInfo = await getAuthorizationInfoAPI();
      if (sessionState?.authorities?.length > 0) {
        // если авторизация происходит по ссылке приглашению, то нужно отправить запрос на подтверждение приглашения
        if (inviteOrganizationUeid && inviteConfirmationId) {
          try {
            await postAcceptInvitationToOrganizationAPI(inviteOrganizationUeid, inviteConfirmationId);
          } catch (error: any) {
            const message = error.type === 'NOT_FOUND'
              ? 'Ссылка недействительна. Получите новую ссылку и перейдите по ней.'
              : 'Произошла ошибка подтверждения приглашения. Попробуйте позже.';
            toast.error(message);
          }
        }
        
        // у пользователя есть активная сессия с некоторыми правами - направляем в агент
        navigate(routes.agent);
      } else if (authorizationInfo?.hasPassword) {
        toast.error('Пользователь с таким номером телефона уже существует.');
      } else {
        onSubmit(authorizationInfo);
      }
    } catch (error: any) {
      if (error.type === 'BAD_REQUEST') {
        // это валидационная ошибка, при этом у нее такой тип, потому что разработчик бэкенда решил,
        // что других ошибок в этом запросе быть не может, поэтому и так нормально
        setValidationError('Неверный формат, номер должен содержать только цифры и иметь длину не меньше 7 символов');
        return;
      }
      if (error.type === 'PHONE_PROCESSING_EXCEPTION') {
        setValidationError('Не удалось отправить СМС на указанный номер. Проверьте правильность введенного номера телефона или повторите попытку позже');
        return;
      }
      toast.error('Произошла ошибка. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (values: any) => {
    startRegistration(values.phone);
  };
  
  const handleBack = () => {
    if (inviteConfirmationId) {
      navigate({
        pathname: routes.acceptInvitation,
        search: createSearchParams({ confirmation: inviteConfirmationId }).toString(),
      });
    } else {
      navigate(routes.main);
    }
  };

  return (
    <FinalForm
      onSubmit={handleSubmit}
      submitting={isLoading}
      pristine
      initialValues={{ phone: undefined }}
    >
      {({ submitting }: { submitting: any; }) => {
        return (
          <div className={styles['form']}>
            <FinalField name="phone">
              {({ value, onChange, error }: { value: string, onChange: any, error: string }) => {
                setIsEmptyPhone(!value || value === '+7')
                return (
                  <PhoneInput
                    value={value}
                    onChange={(value: any) => {
                      setValidationError('');
                      onChange(value);
                    }}
                    label="Номер телефона"
                    error={error || validationError}
                  />
                )
              }}
            </FinalField>
            <ToolBar align="center" className={styles['buttons']}>
              <Button variant="text" disabled={submitting} onClick={handleBack}>
                Назад
              </Button>
              <Button disabled={submitting || isEmptyPhone} type="submit">
                Продолжить
              </Button>
            </ToolBar>
          </div>
        );
      }}
    </FinalForm>
  );
};

export default LoginForm;
